import { EcoaButton } from "@components/common/EcoaButton";
import React from "react";
import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import { Footer } from "@layouts/Footer";
import { useLangContext } from "@layouts/home/LangContext";
import { fontSizes } from "@styles/fontStyle";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { ecoaCaptureApi } from "maven-lib/dist/states/apis/ecoaCapture";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { HbRequestButton } from "@maven-surface/components/base/HbRequestButton";
import { useIntParam, useRoute } from "@maven-surface/hooks/useRoute";
import { ECOA_CAPTURE_CODE } from "@maven-msg/code/ecoaCapture";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

const StyledPreScreeningQuestionnaireFooter = styled.div`
  .ecoa-button button {
    width: 100%;
    min-width: auto;
    max-width: 100%;
    height: 50px;
    padding: 0;
    border-radius: 0;
    font-weight: bold;
    font-size: ${fontSizes["BASE"]};
    text-transform: uppercase;

    .cr-icon {
      font-size: 0.9em;
    }
  }
`;

interface PreScreeningQuestionnaireFooterProps {
  disabled: boolean;
  studyId: bigint;
  siteId?: bigint;
  isLastQuestionnaire: boolean;
  onClickNext: () => void;
  getPreScreeningParams: () => {
    encryptedPayload: string;
    captures: Array<StudyCaptureData>;
  };
}

export function PreScreeningQuestionnaireFooter({
  disabled,
  studyId,
  siteId,
  getPreScreeningParams,
  isLastQuestionnaire,
  onClickNext,
}: PreScreeningQuestionnaireFooterProps) {
  const { pushPath } = useRoute();
  const { fontSize } = useLangContext();
  const params = useParams<{ questionnaireSeq: string }>();
  const questionnaireSeq = useIntParam(params.questionnaireSeq);
  const textSize = fontSize === "LARGE" ? "4X-LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : undefined;

  return (
    <StyledPreScreeningQuestionnaireFooter>
      <Footer>
        {!(questionnaireSeq === 1 && isLastQuestionnaire) && (
          <EcoaButton
            disabled={questionnaireSeq === 1}
            text={MvnMessage(COMMON_LANG.PREV)}
            fontSize={textSize}
            iconLeft="ChromeBack"
            style={{ borderRight: "1px solid rgba(255, 255, 255, 0.2)" }}
            onClick={() => {
              pushPath(`/${studyId}/pre/pre-screening/${questionnaireSeq - 1}`);
              window?.scrollTo(0, 0);
            }}
          />
        )}
        {isLastQuestionnaire ? (
          <HbRequestButton
            disabled={disabled}
            api={{
              ...ecoaCaptureApi.captureEcoaPrescreeningCaptures(studyId),
              data: siteId ? { ...(getPreScreeningParams() as any), siteId } : getPreScreeningParams(),
            }}
            delegate={{
              success: (resp) => {
                if (resp?.studyPreSubject?.reserved1 !== "FAIL") {
                  // PASS or FAIL
                  return pushPath(`/${studyId}/pre/pre-screening/done`, { pass: true });
                }
                return pushPath(`/${studyId}/pre/pre-screening/done`, { pass: false });
              },
              error: (error) =>
                MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_CAPTURE_FAIL, { message: error }, [ECOA_CAPTURE_CODE.ECOA_CAPTURE_DATA_MISSING]),
            }}
            text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_SUBMIT)}
          />
        ) : (
          <HbButton
            disabled={disabled}
            text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
            iconRight="ChromeBackMirrored"
            onClick={() => {
              onClickNext();
            }}
          />
        )}
      </Footer>
    </StyledPreScreeningQuestionnaireFooter>
  );
}
